/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import RouterService from '@ember/routing/router-service';
import { isEmpty } from '@ember/utils';
import Transition from '@ember/routing/-private/transition';

import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import { dropTask, lastValue, task, timeout } from 'ember-concurrency';
import AlertsService from 'cio/services/alerts';
import EnvironmentService from 'cio/services/environment';
import AnalyticsService from 'cio/services/analytics';
import { CredentialData, GistAuthenticationToken } from 'cio/utils/in-app/credentials';
import InAppCredentialsService from 'cio/services/in-app-credentials';
import ENV from 'cio/config/environment';

export type Credential = {
  username?: string;
  password?: string;
  updated?: number;
};

export type CredentialErrors = {
  username?: string[];
  password?: string[];
};

enum TrackedEvents {
  SetttingsViewed = 'settings_viewed',
  EnablingStarted = 'enabling_started',
  EnablingCompleted = 'enabling_completed',
  Disabled = 'disabled',
}

enum Tab {
  Messages = 'Messages',
  Branding = 'Branding',
}
const ALL_TABS = [
  { label: Tab.Messages, value: Tab.Messages },
  { label: Tab.Branding, value: Tab.Branding },
];

interface InAppMessagesArgs {
  messageId?: string;
  onMessageIdChange: (messageId: string) => void;
}

const MESSAGE_DETAIL_ROUTE_NAME = 'in-app-messages.message';
const MESSAGES_LIST_ROUTE_NAME = 'in-app-messages.index';

export default class InAppMessagesPage extends Component<InAppMessagesArgs> {
  @service api!: ApiService;
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;
  @service inAppCredentials!: InAppCredentialsService;

  @tracked isIntegrationEnabled = false;
  @tracked isSettingsViewedTracked = false;
  @tracked currentTab = Tab.Messages;
  @tracked organizationId = '';
  @tracked errors!: CredentialErrors;
  @tracked isFullScreen = false;
  @tracked iframeTransitioning = false;
  @tracked _hideHeader = false;

  get hideHeader() {
    return this.args.messageId !== '' || this._hideHeader;
  }

  headerElement: HTMLElement | null = null;
  editorContainerElement: HTMLElement | null = null;

  get loadCredential() {
    return this.inAppCredentials.loadCredential;
  }

  get loadGistAuthenticationToken() {
    return this.inAppCredentials.loadGistAuthenticationToken;
  }

  @lastValue('loadCredential') credentialData: CredentialData | null = null;
  @lastValue('loadGistAuthenticationToken') gistAuthToken: GistAuthenticationToken | null = null;

  constructor(owner: unknown, args: InAppMessagesArgs) {
    super(owner, args);
    this.loadIntegrationDataFirst.perform();
    this.router.on('routeWillChange', this.onRouteWillChange);
    this.router.on('routeDidChange', this.onRouteDidChange);
  }

  willDestroy() {
    super.willDestroy();
    // @ts-ignore TS complains about "off" not existing, but it does exist
    this.router.off('routeWillChange', this.onRouteWillChange);
    this.router.off('routeDidChange', this.onRouteDidChange);
  }

  @action didInsertEditorContainer(element: HTMLElement) {
    this.editorContainerElement = element;
  }

  @action didInsertHeader(element: HTMLElement) {
    this.headerElement = element;
  }

  @action
  onRouteWillChange(transition: Transition) {
    if (this.editorContainerElement == null) {
      return;
    }

    if (
      (transition.from?.name === MESSAGE_DETAIL_ROUTE_NAME && transition.to.name === MESSAGES_LIST_ROUTE_NAME) ||
      (transition.from?.name === MESSAGES_LIST_ROUTE_NAME && transition.to.name === MESSAGE_DETAIL_ROUTE_NAME)
    ) {
      // Hide UI elements and start the loading spinner via: iframeTransitioning
      this.makeEditorContainerInvisible();
      this._hideHeader =
        transition.from?.name === MESSAGES_LIST_ROUTE_NAME && transition.to.name === MESSAGE_DETAIL_ROUTE_NAME;
      this.iframeTransitioning = true;
    }
  }

  @action
  onRouteDidChange(transition: Transition) {
    if (transition.from?.name === MESSAGE_DETAIL_ROUTE_NAME && transition.to.name === MESSAGES_LIST_ROUTE_NAME) {
      // This code corresponds to the transition that start by clicking the breadcrumb
      // component (navigate back) which is different from the opposite direction where
      // the navigation starts because of the iframe detecting a locationChanged event
      this.makeEditorContainerVisibleTask.perform();
    }
    if (transition.from?.name === MESSAGES_LIST_ROUTE_NAME && transition.to.name === MESSAGE_DETAIL_ROUTE_NAME) {
      this.cancelIframeTransitioningFlag.perform();
    }
  }

  get tabs() {
    return ALL_TABS;
  }

  get isTokenValid() {
    let loadTask = this.inAppCredentials.loadGistAuthenticationToken;
    return !loadTask.isRunning && this.gistAuthToken?.payload != null;
  }

  /* 
    In the event of an error while loading the Gist content, such as failed network requests or similar issues, 
    it's possible that the onIframeDidLoad (see below) callback may not be triggered. To address this scenario, we have implemented 
    an async task with a delay that will remove the spinner (by setting iframeTransitioning = false)
    which will place the iframe again on the screen and force a fresh retrieval of the content based on the current iframe location
  */
  cancelIframeTransitioningFlag = dropTask(async () => {
    if (this.iframeTransitioning) {
      await timeout(ENV.APP.inAppEditorTransitionDelay);
      this.iframeTransitioning = false;
    }
  });

  loadIntegrationDataFirst = dropTask(async () => {
    await this.loadIntegrationData.perform();
    this.selectMessageTab();
  });

  loadIntegrationData = dropTask(async () => {
    let { credential, isIntegrationEnabled } = await this.inAppCredentials.loadCredential.perform();
    this.isIntegrationEnabled = isIntegrationEnabled;
    if (credential != null) {
      this.organizationId = credential?.username ?? '';
    }
    await this.trackSettingsViewed();
    await this.inAppCredentials.loadGistAuthenticationToken.perform();
  });

  disableCredential = dropTask(async () => {
    await this.api.delete('credentials/in_app');
    this.track(TrackedEvents.Disabled);
    await this.reloadCredential();
  });

  @action
  onInAppEnabled() {
    this.reloadCredential();
  }

  private async reloadCredential() {
    await this.environment.reload.perform();
    await this.loadIntegrationData.perform();
  }

  @action
  private async trackSettingsViewed() {
    if (!this.isSettingsViewedTracked) {
      this.isSettingsViewedTracked = true;
      this.track(TrackedEvents.SetttingsViewed);
    }
  }

  @action
  private track(event: TrackedEvents) {
    this.analytics.track('in_app', event, { enabled: this.isIntegrationEnabled });
  }

  @action
  navigateToInAppSettings() {
    this.router.transitionTo('actions.in_app', { queryParams: { showForm: true } });
  }

  @action
  private selectMessageTab() {
    if (this.isIntegrationEnabled) {
      this.currentTab = Tab.Messages;
    }
  }

  @action
  changeTab(to: Tab) {
    if (this.currentTab === to) {
      // no tab change happening. No need to trigger attribute update.
      return;
    }

    if (this.currentTab === Tab.Messages) {
      // reset messageId so message tab do not keep going back to specific message
      this.args.onMessageIdChange?.('');
    }
    this.currentTab = to;
  }

  private makeEditorContainerVisible() {
    this.editorContainerElement?.classList.remove('invisible');
    this.editorContainerElement?.classList.add('visible');
  }

  private makeEditorContainerInvisible() {
    this.editorContainerElement?.classList.add('invisible');
    this.editorContainerElement?.classList.remove('visible');
  }

  @action
  async onIframeDidLoad() {
    this.makeEditorContainerVisibleTask.perform();
    this.iframeTransitioning = false;
  }

  @action
  onMessageIdChange(messageId: string) {
    this.delayNavigationIfNeeded.perform(messageId);
  }

  makeEditorContainerVisibleTask = task(async () => {
    // This delay is to avoid a twitchy/glitchy effect of the previous page (message detail - going to the messages list) in the iframe
    await timeout(ENV.APP.inAppEditorTransitionDelay);
    this.makeEditorContainerVisible();
    this.iframeTransitioning = false;
  });

  delayNavigationIfNeeded = task(async (messageId: string) => {
    if (!isEmpty(messageId)) {
      /* 
        This improves how the transition to full-screen mode works: 
        The timeout causes a delay that prevents the router from transitioning
        to full screen mode, before hiding the UI elements needed  
      */
      await timeout(ENV.APP.inAppFullScreenTransitionDelay);
    }
    /* 
      To handle message deletion, the onMessageIdChange call has been moved out if the conditional
      As a result, when the deletion occurs, the iframe detects a location change and notifies the UI side. 
      The UI side then initiates a navigation back to the message list, ensuring a seamless transition
      and better user experience
    */
    this.args.onMessageIdChange(messageId);
  });
}
